import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  Body1,
  Button,
  FluentProvider,
  Subtitle1,
  makeStyles,
  tokens,
  useThemeClassName,
} from '@fluentui/react-components';
import { prLightTheme } from '../theme/brand';

interface Props {
  children: ReactNode;
  /** Shown in the message so a user can say where it happened. */
  area?: string;
}

const useStyles = makeStyles({
  panel: {
    margin: '16px',
    padding: '16px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // De-emphasised with a token, never opacity — opacity dims text below the
  // contrast ratio the high-contrast theme exists to guarantee.
  detail: { marginTop: '8px', color: tokens.colorNeutralForeground2 },
  message: { marginTop: '8px' },
  action: { marginTop: '12px' },
});

/**
 * The fallback, as its own component so it can ask Fluent whether a theme is
 * already in scope.
 *
 * Two boundaries wrap the app (see `main.tsx`) and they render in different
 * places. The outer one sits above `TeamsProvider`, so when it fires there is
 * no `FluentProvider` at all and every `tokens.*` reference silently resolves
 * to nothing (CLAUDE.md log entry 15) — it has to bring its own. The inner one
 * renders *inside* `TeamsProvider`, where a theme is already applied;
 * unconditionally wrapping there would pin the fallback to the light theme and
 * flash a white panel at a user running Teams in dark or high contrast (#54).
 *
 * `useThemeClassName()` is empty exactly when there is no provider above, which
 * is the distinction to switch on.
 */
function ErrorFallback({
  area,
  message,
  onReload,
}: {
  area?: string;
  message: string;
  onReload: () => void;
}) {
  const styles = useStyles();
  const inheritedTheme = useThemeClassName() !== '';

  const panel = (
    /* A plain region rather than Fluent's MessageBar: MessageBar expects its
       actions in MessageBarActions and misbehaves with a button in the body. */
    <div role="alert" className={styles.panel}>
      <Subtitle1 as="h2">Something went wrong</Subtitle1>
      <Body1 as="p" className={styles.message}>
        {area ? `The ${area} could not be displayed. ` : ''}
        Reload to try again. If it keeps happening, tell IT what you were doing.
      </Body1>
      <Body1 as="p" className={styles.detail}>
        {message}
      </Body1>
      <Button appearance="primary" onClick={onReload} className={styles.action}>
        Reload
      </Button>
    </div>
  );

  if (inheritedTheme) return panel;
  return <FluentProvider theme={prLightTheme}>{panel}</FluentProvider>;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so a crash degrades to a readable message.
 *
 * This matters more inside Teams than on the open web: the tab is an iframe
 * with no address bar and no reload button, so an unhandled error leaves the
 * user staring at a blank white panel with no way forward. React 19 unmounts
 * the whole tree on an uncaught error, so without a boundary there is nothing
 * left to render.
 *
 * Must be a class component — React has no hook equivalent of
 * `getDerivedStateFromError`.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // TODO(sprint-5): forward to Application Insights with the request id.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    globalThis.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <ErrorFallback area={this.props.area} message={error.message} onReload={this.handleReload} />
    );
  }
}
